import { useCallback, useEffect, useState } from 'react'
import type { Dispatch, SetStateAction } from 'react'

type UseCustomerProtectedListInput<TItem> = {
  enabled: boolean
  token: string | null
  fetchItems: (token: string | null) => Promise<TItem[]>
  toErrorMessage: (error: unknown) => string
  invalidSessionMessage?: string
  requireToken?: boolean
}

type UseCustomerProtectedListResult<TItem> = {
  items: TItem[]
  setItems: Dispatch<SetStateAction<TItem[]>>
  loading: boolean
  error: string
  setError: Dispatch<SetStateAction<string>>
  retry: () => void
}

const DEFAULT_INVALID_SESSION_MESSAGE = 'Sessione cliente non valida'

export const useCustomerProtectedList = <TItem>({
  enabled,
  token,
  fetchItems,
  toErrorMessage,
  invalidSessionMessage = DEFAULT_INVALID_SESSION_MESSAGE,
  requireToken = true,
}: UseCustomerProtectedListInput<TItem>): UseCustomerProtectedListResult<TItem> => {
  const [items, setItems] = useState<TItem[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const load = useCallback(
    async (isActive?: () => boolean) => {
      if (requireToken && !token) {
        if (!isActive || isActive()) {
          setError(invalidSessionMessage)
          setItems([])
          setLoading(false)
        }
        return
      }

      if (!isActive || isActive()) {
        setLoading(true)
        setError('')
      }

      try {
        const nextItems = await fetchItems(token)
        if (isActive && !isActive()) return
        setItems(nextItems)
      } catch (nextError) {
        if (isActive && !isActive()) return
        setItems([])
        setError(toErrorMessage(nextError))
      } finally {
        if (!isActive || isActive()) setLoading(false)
      }
    },
    [fetchItems, invalidSessionMessage, requireToken, toErrorMessage, token],
  )

  useEffect(() => {
    if (!enabled) return
    let active = true
    void load(() => active)
    return () => {
      active = false
    }
  }, [enabled, load])

  const retry = () => {
    void load()
  }

  return { items, setItems, loading, error, setError, retry }
}
